'use client';
import React from 'react';
import { Button } from '@benzinga/core-ui';
import { CloseContainer } from '@benzinga/ui';
import { ContentBlock } from '@benzinga/content-manager';

const MoneyBlocks = React.lazy(() => import('../MoneyBlocks').then(module => ({ default: module.MoneyBlocks })));

export interface AdvertorialPopupProps {
  btnText?: string;
  btnLink?: string;
  popup: ContentBlock;
  popupId?: string;
}

export const AdvertorialPopup: React.FC<AdvertorialPopupProps> = ({ btnLink, btnText, popup, popupId }) => {
  const [isVisible, setIsVisible] = React.useState(false);

  const handleOnClose = React.useCallback(() => {
    setIsVisible(false);
  }, []);

  React.useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.keyCode === 27) {
        setIsVisible(false);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [isVisible]);

  React.useEffect(() => {
    const handleShowAdvertorialPopup = (event: CustomEvent) => {
      const eventPopupId = event.detail?.popupId;
      const isForThisPopup = popupId != null ? eventPopupId === popupId : true;

      if (!isVisible && isForThisPopup) {
        setIsVisible(true);
      }
    };
    document.addEventListener('show_advertorial_popup', handleShowAdvertorialPopup as EventListener);
    return () => {
      document.removeEventListener('show_advertorial_popup', handleShowAdvertorialPopup as EventListener);
    };
  }, [isVisible, popupId]);

  const handleBackdropClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleOnClose();
      }
    },
    [handleOnClose],
  );

  return (
    <div
      aria-modal="true"
      className={[
        'fixed inset-0 z-50 bg-black/50 p-4 overflow-y-auto advertorial-popup',
        isVisible ? ' m-0' : 'hidden',
      ].join(' ')}
      onClick={handleBackdropClick}
      role="dialog"
    >
      <div
        className="close-container relative top-[10vh] mx-auto max-w-[750px] bg-white [&_.close-btn]:z-[11] [&_.call-to-action-wrapper]:p-4"
        onClick={e => e.stopPropagation()}
      >
        <CloseContainer onClose={handleOnClose}>
          <h4 className="core-block sticky top-0 mt-0 mb-0 z-[1] bg-white px-4 py-2 border-b border-[#d4d4d4]">
            {popup?.title}
          </h4>
          <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden mb-4 p-4">
            {isVisible && (
              <React.Suspense fallback={null}>
                <MoneyBlocks blocks={popup.blocks} />
              </React.Suspense>
            )}
          </div>
          <div className="sticky bottom-0 left-0 right-0 my-4 p-4 bg-white border-t border-[#d4d4d4]">
            <Button as="a" className="w-full" href={btnLink} variant="flat-blue">
              {btnText ?? 'Learn More'}
            </Button>
          </div>
        </CloseContainer>
      </div>
    </div>
  );
};
